import { MARTINS_POINT_INSURANCE_PROVIDER } from "~/shared/utils/constants";
import { timezone } from "~/shared/utils/timezone";
import { PaginationParams, ServiceResponse } from "./api.types";
import { api } from "./axios";

export interface SpecialtyItem {
  name: string;
}

export interface TimeSlot {
  time: string;
  selected: boolean;
}

export interface ProviderMetadata {
  specialties: string[];
  insurancesCantSee: string[];
  biography: string;
}

export interface Provider {
  id: string;
  fullName: string;
  availableSlots: string[];
  metadata: ProviderMetadata;
  licensedIn: string[];
  avatarUrl: string | null;
  credentials: string;
  isFullTime: boolean;
  matchCount: number;
}

export interface DaysWithAvailability {
  dates: {
    [date: string]: number;
  };
  availabilityForNextMonth: boolean;
}

export interface AppointmentSlot {
  date: Date;
  available: boolean;
}

interface ProviderSearchParams extends PaginationParams {
  location?: string;
  state?: string;
  startDate: string;
  primaryInsurance: string;
  secondaryInsurance?: string;
  patientEmail: string;
  patientId: string;
  conditions: string[];
  preferredOfficeVisit?: string;
  /**
   * Waive the backend's minimum-notice buffer on this search.
   *
   * Set only by the admin booking screen: staff booking on a patient's behalf
   * minutes out is a normal front-desk action. The patient funnel omits it and
   * keeps the buffer. Slots whose time has already passed are dropped either way —
   * that floor is enforced at booking and cannot be waived from here.
   */
  staffBooking?: boolean;
  referringPracticeId?: number | null;
}

interface DaysWithAvailabilityParams {
  location: string | null;
  state?: string;
  startDate: string;
  endDate: string;
  primaryInsurance: string;
  secondaryInsurance?: string;
  patientEmail: string;
  patientId: string;
  conditions: string[];
  preferredOfficeVisit?: string;
  /** See ProviderSearchParams.staffBooking — the day badges must match the list. */
  staffBooking?: boolean;
  /** Same restriction as the provider list, so the day badges agree with it. */
  referringPracticeId?: number | null;
}

const getProviderName = (providerName: string) => {
  if (providerName === "Martin's Point") {
    return MARTINS_POINT_INSURANCE_PROVIDER;
  }

  return providerName;
};

export const getProviders = async ({
  location,
  state,
  page = 1,
  perPage = 10,
  startDate,
  patientEmail,
  patientId,
  primaryInsurance,
  secondaryInsurance,
  conditions = [],
  preferredOfficeVisit,
  staffBooking,
  referringPracticeId,
}: ProviderSearchParams) => {
  const apiPrimaryInsurance = getProviderName(primaryInsurance);
  const apiSecondaryInsurance = secondaryInsurance
    ? getProviderName(secondaryInsurance)
    : null;

  const { data } = await api.get<ServiceResponse<Provider[]>>(
    "providers/by-specializations",
    {
      params: {
        state,
        page,
        perPage,
        startDate,
        endDate: startDate,
        preferredOfficeVisit: location ?? preferredOfficeVisit,
        timezone: timezone,
        patientEmail,
        patientId,
        conditions,
        // 1, not true: axios serializes a boolean to the string "true", which
        // Laravel's `boolean` rule rejects (it accepts only [true, false, 0, 1,
        // '0', '1']), 422-ing the whole search. Omitted entirely when false.
        ...(staffBooking && { staffBooking: 1 }),
        "insurance[primary]": apiPrimaryInsurance,
        ...(apiSecondaryInsurance && {
          "insurance[secondary]": apiSecondaryInsurance,
        }),
        ...(referringPracticeId != null && { referringPracticeId }),
      },
    },
  );
  return data.data;
};

export const getDaysWithAvailability = async ({
  location,
  state,
  startDate,
  endDate,
  primaryInsurance,
  secondaryInsurance,
  patientEmail,
  patientId,
  conditions = [],
  preferredOfficeVisit,
  staffBooking,
  referringPracticeId,
}: DaysWithAvailabilityParams) => {
  const apiPrimaryInsurance = getProviderName(primaryInsurance);
  const apiSecondaryInsurance = secondaryInsurance
    ? getProviderName(secondaryInsurance)
    : null;
  const { data } = await api.get<ServiceResponse<DaysWithAvailability>>(
    "providers/availabilities-for-entire-week",
    {
      params: {
        state,
        startDate,
        endDate,
        preferredOfficeVisit: location ?? preferredOfficeVisit,
        timezone: timezone,
        patientEmail,
        patientId,
        conditions,
        // 1, not true: axios serializes a boolean to the string "true", which
        // Laravel's `boolean` rule rejects (it accepts only [true, false, 0, 1,
        // '0', '1']), 422-ing the whole search. Omitted entirely when false.
        ...(staffBooking && { staffBooking: 1 }),
        ...(referringPracticeId != null && { referringPracticeId }),
        "insurance[primary]": apiPrimaryInsurance,
        ...(apiSecondaryInsurance && {
          "insurance[secondary]": apiSecondaryInsurance,
        }),
      },
    },
  );

  return data.data;
};
